import { useTranslations } from '@vocab/react';
import {
  ButtonLink,
  Dialog,
  Stack,
  Actions,
  IconNewWindow,
  Text,
  IconPersonAdd,
  IconPersonVerified,
  IconVisibility,
  Column,
  Columns,
} from 'braid-design-system';
import { useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useSeekPassUrl } from 'src/modules/hooks/useSeekPassUrl';

import translations from './.vocab';
import { ReferenceIcon } from './ReferenceIcon';

interface AddReferenceDialogProps {
  showAddReferenceDialog: boolean;
  setShowAddReferenceDialog: (value: boolean) => void;
}

export const AddReferenceDialog = ({
  showAddReferenceDialog,
  setShowAddReferenceDialog,
}: AddReferenceDialogProps) => {
  const { t } = useTranslations(translations);
  const { referenceDialogPressed, referenceDialogDisplayed } = useAnalytics();
  const seekPassUrl = useSeekPassUrl(
    'https://app.seekpass.co/reference-check/start',
  );

  useEffect(() => {
    if (showAddReferenceDialog) {
      referenceDialogDisplayed({});
    }
  }, [showAddReferenceDialog, referenceDialogDisplayed]);

  const onContinueToSeekPassClicked = () => {
    referenceDialogPressed({
      callToAction: 'continue to seek pass',
    });
  };
  const onLearnMoreClicked = () => {
    referenceDialogPressed({
      callToAction: 'learn more',
    });
  };

  return (
    <Dialog
      id="reference-info-dialog"
      title={t('References')}
      description={null}
      illustration={<ReferenceIcon />}
      open={showAddReferenceDialog}
      onClose={() => setShowAddReferenceDialog(false)}
      closeLabel={t('Close')}
    >
      <Stack space="large" align="center">
        <Stack space="large" align="left">
          <Text>{t('Add credibility to your profile...')}</Text>
          <Columns space="small" alignY="center">
            <Column width="content">
              <IconPersonAdd />
            </Column>
            <Column>
              <Text>
                {t(
                  'Nominate referees to complete a standard set of online questions.',
                )}
              </Text>
            </Column>
          </Columns>
          <Columns space="small" alignY="center">
            <Column width="content">
              <IconPersonVerified />
            </Column>
            <Column>
              <Text>
                {t('Easily reuse references for future job applications.')}
              </Text>
            </Column>
          </Columns>
          <Columns space="small" alignY="center">
            <Column width="content">
              <IconVisibility />
            </Column>
            <Column>
              <Text>{t('Only employers can see the references...')}</Text>
            </Column>
          </Columns>
        </Stack>
        <Actions>
          <Stack space="small" align="center">
            <ButtonLink
              icon={<IconNewWindow />}
              iconPosition="trailing"
              href={seekPassUrl}
              onClick={onContinueToSeekPassClicked}
              target="_blank"
              rel="noopener"
              role="button"
              tone="formAccent"
            >
              {t('Continue to SEEK Pass')}
            </ButtonLink>
            <ButtonLink
              variant="transparent"
              tone="formAccent"
              target="_blank"
              onClick={onLearnMoreClicked}
              href="https://help.seekpass.co/hc/"
            >
              {t('Learn more')}
            </ButtonLink>
          </Stack>
        </Actions>
      </Stack>
    </Dialog>
  );
};
